import type { ComponentType } from 'react';
import {
	createBrowserRouter,
	Navigate,
	Outlet,
	type RouteObject,
} from 'react-router-dom';
import { RethrowRouteError } from '@/components/error/RethrowRouteError';
import { PageLoader } from '@/components/feedback/PageLoader';
import { AppLayout } from '@/components/layout/AppLayout';
import { RequireAuth } from './guards/RequireAuth';
import { ROUTES } from '@/constants';

/**
 * Route-level code splitting: the router loads a page's chunk before it
 * navigates, so the old page stays while `useNavigation()` reports
 * "loading" (the shell shows progress) instead of a silent wait.
 */
function page<M extends Record<K, ComponentType>, K extends keyof M & string>(
	load: () => Promise<M>,
	name: K,
): () => Promise<{ Component: ComponentType }> {
	return async () => ({ Component: (await load())[name] });
}

/** A public placeholder page, until its content is ready. */
function underDevelopment(title: string) {
	return async () => {
		const { UnderDevelopmentPage } =
			await import('@/app/pages/UnderDevelopmentPage');
		return { Component: () => <UnderDevelopmentPage title={title} /> };
	};
}

const routes: RouteObject[] = [
	{
		path: ROUTES.login,
		lazy: page(() => import('@/features/auth'), 'LoginPage'),
	},
	{
		// Public: invitees have no session yet.
		path: 'invite/:token',
		lazy: page(() => import('@/features/users'), 'AcceptInvitationPage'),
	},
	{
		// Signed-in pages without the app shell (own tab or window).
		element: (
			<RequireAuth>
				<Outlet />
			</RequireAuth>
		),
		children: [
			{
				// Fullscreen playback, opened in its own tab without the app shell.
				path: 'media/:layoutId/preview',
				lazy: page(() => import('@/features/media'), 'LayoutPreviewPage'),
			},
			{
				path: 'playlists/:playlistId/preview',
				lazy: page(() => import('@/features/playlists'), 'PlaylistPreviewPage'),
			},
			{
				// User guide, opened in its own tab from the header.
				path: 'guide',
				lazy: page(() => import('@/features/user-guide'), 'GuidePage'),
			},
			{
				// The Canva window: picks a design for the media dialog that opened it.
				path: 'integrations/canva/pick',
				lazy: page(() => import('@/features/canva'), 'CanvaPickPage'),
			},
			{
				// Canva sends the window back here after sign-in (registered redirect URL).
				path: 'integrations/canva/callback',
				lazy: page(() => import('@/features/canva'), 'CanvaCallbackPage'),
			},
			{
				// The Canva editor's Return button lands here (Developer Portal →
				// Return navigation → Return URL).
				path: 'integrations/canva/return',
				lazy: page(() => import('@/features/canva'), 'CanvaReturnPage'),
			},
		],
	},
	{
		// Standalone device simulator: no app shell and no CMS auth guard, because
		// the runtime keeps going on a device identity after the user session ends.
		path: 'device-simulator/:simulatorId',
		lazy: page(
			() => import('@/features/device-simulator'),
			'DeviceSimulatorPage',
		),
	},
	{
		// Opened on a phone from a device's QR code. Its own sign-in (not the CMS
		// session, no IP allowlist) that can only read kiosk exit codes.
		path: 'kiosk/:displayId',
		lazy: page(() => import('@/features/kiosk-access'), 'KioskCodePage'),
	},
	// Public pages (linked from app listings and emails): no session required.
	// Placeholders until their content is ready.
	{
		path: ROUTES.termsAndConditions,
		lazy: underDevelopment('Terms and Conditions'),
	},
	{
		path: ROUTES.privacyPolicy,
		lazy: underDevelopment('Privacy Policy'),
	},
	{
		path: ROUTES.support,
		lazy: underDevelopment('Support'),
	},
	// Customer portal: public pages, then the guarded area. It never uses the
	// CMS session or the CMS shell.
	{
		path: 'portal/login',
		lazy: page(() => import('@/features/portal'), 'PortalLoginPage'),
	},
	{
		path: 'portal/invite/:token',
		lazy: page(() => import('@/features/portal'), 'PortalAcceptInvitationPage'),
	},
	{
		path: 'portal/reset-password',
		lazy: page(() => import('@/features/portal'), 'PortalResetPasswordPage'),
	},
	{
		path: 'portal/reset-password/:token',
		lazy: page(
			() => import('@/features/portal'),
			'PortalResetPasswordConfirmPage',
		),
	},
	{
		path: ROUTES.portal,
		lazy: page(() => import('@/features/portal'), 'PortalShell'),
		children: [
			{
				index: true,
				lazy: page(() => import('@/features/portal'), 'PortalDashboardPage'),
			},
			{
				path: 'reports',
				lazy: page(() => import('@/features/portal'), 'PortalReportsPage'),
			},
			{
				path: 'settings',
				lazy: page(() => import('@/features/portal'), 'PortalSettingsPage'),
			},
			{ path: '*', element: <Navigate to={ROUTES.portal} replace /> },
		],
	},
	{
		path: ROUTES.home,
		element: (
			<RequireAuth>
				<AppLayout />
			</RequireAuth>
		),
		children: [
			{
				index: true,
				lazy: page(() => import('@/features/dashboard'), 'DashboardPage'),
			},
			{
				path: 'displays',
				lazy: page(() => import('@/features/displays'), 'DisplaysPage'),
			},
			{
				path: 'displays/power-plans/new',
				lazy: page(
					() => import('@/features/power-plans'),
					'PowerPlanEditorPage',
				),
			},
			{
				path: 'displays/power-plans/:planId',
				lazy: page(
					() => import('@/features/power-plans'),
					'PowerPlanEditorPage',
				),
			},
			{
				path: 'buildings',
				lazy: page(() => import('@/features/floor-plans'), 'BuildingsPage'),
			},
			{
				path: 'buildings/:buildingId',
				lazy: page(() => import('@/features/floor-plans'), 'BuildingPage'),
			},
			{
				path: 'buildings/:buildingId/floors/:floorId',
				lazy: page(() => import('@/features/floor-plans'), 'BuildingPage'),
			},
			{
				path: 'media',
				lazy: page(() => import('@/features/media'), 'MediaPage'),
			},
			{
				path: 'customers',
				lazy: page(() => import('@/features/customers'), 'CustomersPage'),
			},
			{
				path: 'playlists',
				lazy: page(() => import('@/features/playlists'), 'PlaylistsPage'),
			},
			{
				path: 'playlists/:playlistId',
				lazy: page(() => import('@/features/playlists'), 'PlaylistEditorPage'),
			},
			{
				path: 'schedules',
				lazy: page(() => import('@/features/schedules'), 'SchedulesPage'),
			},
			{
				path: 'schedules/:scheduleId',
				lazy: page(() => import('@/features/schedules'), 'ScheduleEditorPage'),
			},
			{
				path: 'media/:layoutId/layout',
				lazy: page(() => import('@/features/media'), 'LayoutEditorPage'),
			},
			{
				path: 'settings',
				lazy: page(() => import('@/app/pages/settings'), 'SettingsLayout'),
				children: [
					{
						index: true,
						element: <Navigate to={ROUTES.settingsAuthentication} replace />,
					},
					{
						path: 'authentication',
						lazy: page(
							() => import('@/app/pages/settings'),
							'AuthenticationSettingsPage',
						),
					},
					{
						path: 'notifications',
						lazy: page(
							() => import('@/app/pages/settings'),
							'NotificationSettingsPage',
						),
					},
					{
						path: 'app-update',
						lazy: page(
							() => import('@/app/pages/settings'),
							'AppUpdateSettingsPage',
						),
					},
					{
						path: 'vendor-sdks',
						lazy: page(
							() => import('@/app/pages/settings'),
							'VendorSdkSettingsPage',
						),
					},
					{
						path: '*',
						element: <Navigate to={ROUTES.settingsAuthentication} replace />,
					},
				],
			},
			{
				path: 'audit',
				lazy: page(() => import('@/features/audit'), 'AuditPage'),
			},
			{
				path: 'reports',
				lazy: page(() => import('@/features/reports'), 'ReportsPage'),
			},
			{
				path: 'users',
				lazy: page(() => import('@/features/users'), 'UsersPage'),
			},
			{
				path: 'tenants',
				lazy: page(() => import('@/features/tenants'), 'TenantsPage'),
			},
			{
				path: '*',
				lazy: page(() => import('@/app/pages/NotFoundPage'), 'NotFoundPage'),
			},
		],
	},
	{ path: '*', element: <Navigate to={ROUTES.home} replace /> },
];

export const router = createBrowserRouter([
	{
		// Route errors (e.g. a chunk missing after a deployment) go to
		// AppErrorBoundary instead of the router's built-in error page.
		errorElement: <RethrowRouteError />,
		// The first page's chunk is loading.
		hydrateFallbackElement: <PageLoader fullScreen />,
		children: routes,
	},
]);
